
import React, { useState } from 'react';
import { Camera } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar } from '@/components/ui/avatar';
import { uploadImageToStorage } from '@/utils/imageProcessing';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface ProfilePhotoUploadProps {
  currentPhotoUrl?: string;
  userId: string;
  userName: string;
  onPhotoChange: (url: string) => void;
}

export const ProfilePhotoUpload: React.FC<ProfilePhotoUploadProps> = ({ 
  currentPhotoUrl, 
  userId,
  userName,
  onPhotoChange
}) => {
  const [uploading, setUploading] = useState(false);
  
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      setUploading(true);
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }
      
      // Check file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image file size should be less than 2MB');
        return;
      }
      
      // Create a sanitized filename based on user name
      const sanitizedName = userName.toLowerCase().replace(/[^a-z0-9]/g, '-');
      const fileName = `profile-${sanitizedName}-${Date.now()}.${file.name.split('.').pop()}`;
      
      // Create the File object with the new name
      const renamedFile = new File([file], fileName, { type: file.type });
      
      // Upload to storage
      const uploadedUrl = await uploadImageToStorage(renamedFile, 'profile-photos');
      
      // Update user profile
      const { error } = await supabase
        .from('user_profiles')
        .update({ avatar_url: uploadedUrl })
        .eq('id', userId);
        
      if (error) throw error;
      
      onPhotoChange(uploadedUrl);
      toast.success('Profile photo updated successfully');
    } catch (error) {
      console.error('Error uploading profile photo:', error);
      toast.error('Failed to upload profile photo');
    } finally {
      setUploading(false);
    }
  };
  
  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium">Profile Photo</h3>
          
          <Button 
            variant="outline" 
            size="sm" 
            disabled={uploading}
            onClick={() => document.getElementById('profile-photo-input')?.click()}
            className="flex items-center gap-2"
          >
            <Camera className="h-4 w-4" />
            <span>{currentPhotoUrl ? 'Change' : 'Upload'} Photo</span>
          </Button>
          
          <input 
            type="file" 
            id="profile-photo-input" 
            accept="image/*" 
            className="hidden" 
            onChange={handleFileChange}
            disabled={uploading}
          />
        </div>
        
        <div className="flex justify-center">
          {currentPhotoUrl ? (
            <Avatar className="h-24 w-24 border-2 border-gray-200">
              <img src={currentPhotoUrl} alt={userName} className="h-full w-full object-cover" />
            </Avatar>
          ) : (
            <Avatar className="h-24 w-24 bg-edgefi-amethyst/20 text-edgefi-purple border-2 border-gray-200 text-3xl">
              <span className="font-bold">{userName[0]}</span>
            </Avatar>
          )}
        </div>
        
        {uploading && (
          <div className="text-center text-sm text-gray-500">
            Uploading photo...
          </div>
        )}
        
        <p className="text-xs text-gray-500 text-center">
          For best results, use a square image of at least 256x256 pixels
        </p>
      </CardContent>
    </Card>
  );
};
